import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Home as HomeIcon, PiggyBank, Wallet, Calculator,
  BarChart3, LogOut, User
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Cell, PieChart
} from "recharts";
import { LOGO_URL } from "@/config";
import formatCurrency from "@/utils/formatCurrency";
import CurrencyInput from "@/components/shared/CurrencyInput";

// Static chart config (module-level, stable reference avoids unnecessary re-renders)
const Y_AXIS_TICK = { fontSize: 13 };
const X_AXIS_TICK = { fontSize: 12 };
const BAR_RADIUS = [0, 8, 8, 0];
const LINE_DOT_BLUE = { r: 6, fill: '#3b82f6' };
const LINE_DOT_RED = { r: 6, fill: '#ef4444' };
const ASSET_COLORS = {
  'Equity': '#3b82f6',
  'Debt / Fixed Deposit': '#22c55e',
  'Gold': '#f59e0b',
  'Real Estate': '#8b5cf6',
};
const ASSET_DOT_CLASSES = {
  'Equity': 'bg-blue-500',
  'Debt / Fixed Deposit': 'bg-green-500',
  'Gold': 'bg-amber-500',
  'Real Estate': 'bg-purple-500',
};

const PortfolioCalculator = ({ investmentAssets, user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePage, setActivePage] = useState(1);

  // Derive initial amounts from Net Worth data
  const totalEquity = (investmentAssets?.equity_assets || []).reduce((s, a) => s + (a.amount || 0), 0);
  const totalDebt = (investmentAssets?.debt_assets || []).reduce((s, a) => s + (a.amount || 0), 0);
  const totalGold = (investmentAssets?.gold_assets || []).reduce((s, a) => s + (a.amount || 0), 0);
  const totalRealEstate = (investmentAssets?.real_estate_assets || []).reduce((s, a) => s + (a.amount || 0), 0);

  const [portfolioData, setPortfolioData] = useState([
    { asset: 'Equity', amount: totalEquity, returnRate: 13 },
    { asset: 'Debt / Fixed Deposit', amount: totalDebt, returnRate: 6 },
    { asset: 'Gold', amount: totalGold, returnRate: 10 },
    { asset: 'Real Estate', amount: totalRealEstate, returnRate: 8 }
  ]);

  // Sync with Net Worth when it changes
  useState(() => {
    setPortfolioData([
      { asset: 'Equity', amount: totalEquity, returnRate: portfolioData[0]?.returnRate || 13 },
      { asset: 'Debt / Fixed Deposit', amount: totalDebt, returnRate: portfolioData[1]?.returnRate || 6 },
      { asset: 'Gold', amount: totalGold, returnRate: portfolioData[2]?.returnRate || 10 },
      { asset: 'Real Estate', amount: totalRealEstate, returnRate: portfolioData[3]?.returnRate || 8 }
    ]);
  });

  const [idealReturn, setIdealReturn] = useState(12);

  const totalInvestment = portfolioData.reduce((sum, item) => sum + item.amount, 0);

  const portfolioWithCalcs = portfolioData.map(item => ({
    ...item,
    allocation: totalInvestment > 0 ? (item.amount / totalInvestment) * 100 : 0,
    weightedReturn: totalInvestment > 0 ? (item.amount * item.returnRate) / totalInvestment : 0
  }));

  const overallReturn = portfolioWithCalcs.reduce((sum, item) => sum + item.weightedReturn, 0);

  const updatePortfolioItem = (index, field, value) => {
    const updated = [...portfolioData];
    updated[index] = { ...updated[index], [field]: value };
    setPortfolioData(updated);
  };

  const formatDecimalCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(1)} L`;
    return `₹${formatCurrency(num)}`;
  };
  const allocationChartData = portfolioWithCalcs.map(item => ({
    asset: item.asset,
    allocation: parseFloat(item.allocation.toFixed(2)),
    fill: ASSET_COLORS[item.asset] || '#64748b'
  }));
  
  const years = [0, 5, 10, 15, 20, 25, 30];
  const growthData = years.map(year => {
    const currentValue = totalInvestment * Math.pow(1 + overallReturn / 100, year);
    const idealValue = totalInvestment * Math.pow(1 + idealReturn / 100, year);
    return { year: `${year}Y`, current: Math.round(currentValue), ideal: Math.round(idealValue) };
  });

  return (
    <div className="finance-app min-h-screen p-3 sm:p-6 lg:p-8" data-testid="portfolio-calculator">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-8 gap-2">
          <div className="flex items-center shrink-0">
            <img src={LOGO_URL} alt="SubiWealth" className="h-8 sm:h-12" />
          </div>
          <h1 className="text-base sm:text-2xl font-bold text-teal-600">Portfolio Calculator</h1>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="relative">
              <button onClick={() => setMenuOpen(!menuOpen)} className="flex flex-col gap-1 p-2 hover:bg-white/50 rounded-lg transition-colors">
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
                <span className="w-5 h-0.5 bg-teal-700 rounded"></span>
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}></div>
                  <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 min-w-[200px]">
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left" onClick={() => { navigate("/"); setMenuOpen(false); }}><HomeIcon size={18} /> Dashboard</button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left" onClick={() => { navigate("/budget-planner"); setMenuOpen(false); }}><PiggyBank size={18} /> Budget Planner</button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left" onClick={() => { navigate("/net-worth"); setMenuOpen(false); }}><Wallet size={18} /> Net Worth</button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left" onClick={() => { navigate("/calculators"); setMenuOpen(false); }}><Calculator size={18} /> Calculators</button>
                    <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-teal-50 text-gray-700 hover:text-teal-600 transition-colors text-left" onClick={() => { navigate("/analytics"); setMenuOpen(false); }}><BarChart3 size={18} /> Analytics</button>
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100">
              <User size={16} className="text-teal-600" />
              <span className="text-gray-700 font-medium">{user?.name}</span>
              <button className="ml-1 text-gray-400 hover:text-red-500 transition-colors" onClick={onLogout} title="Logout"><LogOut size={16} /></button>
            </div>
          </div>
        </div>

        {/* Page Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-xl p-1 shadow-sm border border-gray-200 flex">
            <button onClick={() => setActivePage(1)} className={`px-6 py-3 rounded-lg font-medium transition-all ${activePage === 1 ? 'bg-teal-500 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Portfolio Returns Calculator</button>
            <button onClick={() => setActivePage(2)} className={`px-6 py-3 rounded-lg font-medium transition-all ${activePage === 2 ? 'bg-teal-500 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}>Portfolio Growth Comparison</button>
          </div>
        </div>

        {/* PAGE 1 */}
        {activePage === 1 && (
          <div className="space-y-6">
            <div className="finance-card">
              <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <TrendingUp className="text-teal-600" size={24} /> Portfolio Returns Calculator
              </h2>
              <div className="table-scroll">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="bg-teal-50">
                      <th className="text-left px-4 py-3 font-semibold text-teal-700 rounded-tl-lg">Asset Type</th>
                      <th className="text-right px-4 py-3 font-semibold text-teal-700">Investment Amount (₹)</th>
                      <th className="text-right px-4 py-3 font-semibold text-teal-700">Expected Return (%)</th>
                      <th className="text-right px-4 py-3 font-semibold text-teal-700">% Allocation</th>
                      <th className="text-right px-4 py-3 font-semibold text-teal-700 rounded-tr-lg">Weighted Return (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portfolioWithCalcs.map((item, index) => (
                      <tr key={item.asset} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${ASSET_DOT_CLASSES[item.asset] || 'bg-gray-400'}`}></div>
                            <span className="font-medium text-gray-800">{item.asset}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <CurrencyInput value={item.amount} onChange={(v) => updatePortfolioItem(index, 'amount', v)} className="w-36 text-right px-3 py-2 border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-2 focus:ring-teal-100" />
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <input type="number" value={item.returnRate} onChange={(e) => updatePortfolioItem(index, 'returnRate', Number(e.target.value) || 0)} className="w-20 text-right px-3 py-2 border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-2 focus:ring-teal-100" step="0.5" />
                            <span className="text-gray-500">%</span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right"><span className="font-semibold text-blue-600">{item.allocation.toFixed(2)}%</span></td>
                        <td className="px-4 py-4 text-right"><span className="font-semibold text-green-600">{item.weightedReturn.toFixed(2)}%</span></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td className="px-4 py-4 font-bold text-gray-800 rounded-bl-lg">Total</td>
                      <td className="px-4 py-4 text-right font-bold text-gray-800">{formatDecimalCurrency(totalInvestment)}</td>
                      <td className="px-4 py-4"></td>
                      <td className="px-4 py-4 text-right font-bold text-blue-600">100%</td>
                      <td className="px-4 py-4 text-right rounded-br-lg"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="mt-6 p-6 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl text-center">
                <p className="text-teal-100 mb-2">Overall Portfolio Return</p>
                <p className="text-5xl font-bold text-white">{overallReturn.toFixed(2)}%</p>
              </div>
            </div>

            <div className="finance-card">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">% Allocation of Portfolio in Different Asset Classes</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={allocationChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="asset" width={150} tick={Y_AXIS_TICK} />
                  <Tooltip formatter={(v) => `${v.toFixed(2)}%`} />
                  <Bar dataKey="allocation" radius={BAR_RADIUS} label={{ position: 'right', formatter: (v) => `${v.toFixed(1)}%` }}>
                    {allocationChartData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* PAGE 2 */}
        {activePage === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="finance-card text-center">
                <p className="text-gray-500 text-sm mb-2">Initial Investment</p>
                <p className="text-3xl font-bold text-teal-600">{formatDecimalCurrency(totalInvestment)}</p>
                <p className="text-xs text-gray-400 mt-1">(Auto-linked from Portfolio)</p>
              </div>
              <div className="finance-card text-center">
                <p className="text-gray-500 text-sm mb-2">Current Portfolio Return</p>
                <p className="text-3xl font-bold text-blue-600">{overallReturn.toFixed(2)}%</p>
                <p className="text-xs text-gray-400 mt-1">(Auto-calculated)</p>
              </div>
              <div className="finance-card text-center">
                <p className="text-gray-500 text-sm mb-2">Ideal Return</p>
                <div className="flex items-center justify-center gap-2">
                  <input type="number" value={idealReturn} onChange={(e) => setIdealReturn(Number(e.target.value) || 0)} className="w-20 text-center text-2xl font-bold text-red-600 border-b-2 border-red-300 focus:border-red-500 outline-none bg-transparent" step="0.5" />
                  <span className="text-2xl font-bold text-red-600">%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">(Editable)</p>
              </div>
            </div>

            <div className="finance-card">
              <h3 className="text-lg font-semibold text-gray-800 mb-6">Comparison of Portfolio Growth: Current Portfolio Return vs Ideal Returns</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={growthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" tick={X_AXIS_TICK} />
                  <YAxis tickFormatter={(v) => { if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`; if (v >= 100000) return `₹${(v / 100000).toFixed(0)}L`; return `₹${(v / 1000).toFixed(0)}K`; }} />
                  <Tooltip formatter={(value, name) => [formatDecimalCurrency(value), name]} labelFormatter={(label) => `Year ${label}`} />
                  <Legend />
                  <Line type="monotone" dataKey="current" name="Current Portfolio Return" stroke="#3b82f6" strokeWidth={3} dot={LINE_DOT_BLUE} label={{ position: 'top', formatter: (v) => formatDecimalCurrency(v), fontSize: 10 }} />
                  <Line type="monotone" dataKey="ideal" name="Ideal Return" stroke="#ef4444" strokeWidth={3} dot={LINE_DOT_RED} label={{ position: 'bottom', formatter: (v) => formatDecimalCurrency(v), fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="finance-card">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Projected Values Over Time</h3>
              <div className="table-scroll">
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Time Horizon</th>
                      <th className="px-4 py-3 text-right font-semibold text-blue-600">Current Return ({overallReturn.toFixed(1)}%)</th>
                      <th className="px-4 py-3 text-right font-semibold text-red-600">Ideal Return ({idealReturn}%)</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {growthData.map((row) => (
                      <tr key={row.year} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{row.year.replace('Y', ' Years')}</td>
                        <td className="px-4 py-3 text-right text-blue-600 font-semibold">{formatDecimalCurrency(row.current)}</td>
                        <td className="px-4 py-3 text-right text-red-600 font-semibold">{formatDecimalCurrency(row.ideal)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${row.ideal - row.current > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {row.ideal - row.current > 0 ? '-' : '+'}{formatDecimalCurrency(Math.abs(row.ideal - row.current))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioCalculator;
